import { useMappingStore } from "@/stores/mapping";
import { Settings2, X } from "lucide-react";
import {
  useCellPeriod,
  useUpdateCellPeriod,
} from "@/components/modals/mapping-info/hooks";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import { cn } from "@/lib/utils";
import { useWorkbookId } from "@/hooks/use-workbook";
import { PopoverContent } from "@/styles/ui/popover";
import { PopoverClose } from "@radix-ui/react-popover";
import { useTableId } from "../hooks";
import { useModalStore } from "@/stores/modal";
import { useRestoreSelection } from "@/jspreadsheet/jspreadsheet";
import { useMainStore } from "@/stores/main";
import usePeriodDragTagging from "@/hooks/mutations/use-drag-period";
import PeriodDragTaggingModal from "@/components/modals/drag-period";
import { useShouldDragTag } from "@/hooks/mutations/use-drag-tag";

// 타입 정의
interface TriggerProps {
  isOpen: boolean;
}

interface ToggleButtonProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  className?: string;
}

interface ToggleGroupProps {
  title: string;
  options: Array<{
    label: string;
    value: string;
    isActive: boolean;
    onClick: () => void;
  }>;
  containerClassName?: string;
  isLoading?: boolean;
}

// 토글 버튼 컴포넌트
const ToggleButton = ({
  label,
  isActive,
  onClick,
  className,
}: ToggleButtonProps) => {
  return (
    <div
      className={cn(
        "flex py-[6px] px-[8px] justify-center items-center gap-[2px] rounded-[4px] cursor-pointer transition-colors",
        isActive
          ? "bg-white shadow-md"
          : "hover:bg-neutral-200 active:bg-neutral-300",
        className,
      )}
      onClick={onClick}
    >
      <span className="text-primary label-1-normal-medium">{label}</span>
    </div>
  );
};

// 토글 버튼 그룹 컴포넌트
const ToggleGroup = ({
  title,
  options,
  containerClassName,
  isLoading,
}: ToggleGroupProps) => {
  return (
    <div className="flex flex-col items-start gap-[8px] self-stretch">
      <span className="text-primary caption-2-semibold">{title}</span>
      <div
        className={cn(
          "flex p-[2px] w-[268px] items-center gap-[4px] rounded-[4px] bg-neutral-100 transition-opacity duration-100 delay-300",
          isLoading && "opacity-50",
          containerClassName,
        )}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.value}
            label={option.label}
            isActive={option.isActive}
            onClick={option.onClick}
            className={options.length === 3 ? "flex-1" : "w-[130px]"}
          />
        ))}
      </div>
    </div>
  );
};

const MappingCellPropertyPopoverTrigger = ({ isOpen }: TriggerProps) => {
  return (
    <div
      className={cn(
        "w-[28px] h-[28px] flex items-center justify-center flex-shrink-0 gap-[10px] aspect-square rounded-[4px] bg-transparent hover:bg-black/[0.08] active:bg-black/[0.12] cursor-pointer text-primary transition-all duration-100",
        isOpen &&
          "bg-blue-50 text-blue-500 hover:bg-blue-50/90 active:bg-blue-50/80",
      )}
    >
      <Settings2 className="w-[16px] h-[16px] flex-shrink-0 aspect-square" />
    </div>
  );
};

type BalanceTimingItem = "기초" | "기말" | "구분없음";

const itemToAccountingPeriod = (item: BalanceTimingItem) => {
  switch (item) {
    case "기초":
      return AccountingPeriod.BEGIN;
    case "기말":
      return AccountingPeriod.END;
    default:
      return null;
  }
};

type DurationItem = "당기" | "전기" | "구분없음";

const durationItemToTargetYear = (item: DurationItem) => {
  switch (item) {
    case "당기":
      return TargetYear.CURRENT;
    case "전기":
      return TargetYear.PRIOR;
    default:
      return null;
  }
};

type AccumulationItem = "3개월" | "누적" | "구분없음";

const itemToAccumulation = (item: AccumulationItem) => {
  switch (item) {
    case "3개월":
      return Accumulated.QUARTER_ACC;
    case "누적":
      return Accumulated.ACC;
    default:
      return null;
  }
};

const MappingCellPropertyPopoverContent = () => {
  // 상태 관리
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { data: workbookId } = useWorkbookId();
  const {
    data: { tableId },
  } = useTableId();

  const { cellTags, isFetching } = useCellPeriod({
    workbookId,
    tableId,
    selectedFact,
  });

  const { putPeriodToCell: handleSetPeriod, isPuttingPeriod } =
    useUpdateCellPeriod({
      workbookId,
      tableId,
      selectedFact,
    });

  const shouldPeriodDragTagging = useShouldDragTag();
  const { push, pop } = useModalStore((state) => state.modals);
  const currentSelection = useMainStore((state) => state.selection);
  const { restoreSelection } = useRestoreSelection();
  const { mutate: periodDragTagging } = usePeriodDragTagging({
    onSuccess: () => {
      pop();
    },
    onError: () => {
      pop();
    },
  });

  // 공통 이벤트 핸들러 생성기
  const createPeriodHandler =
    (params: {
      accountingPeriod?: AccountingPeriod | null;
      targetYear?: TargetYear | null;
      accumulated?: Accumulated | null;
    }) =>
    () => {
      if (selectedFact && workbookId && tableId) {
        if (shouldPeriodDragTagging) {
          push(PeriodDragTaggingModal, {
            cellAddresses: currentSelection.current?.address,
            period: params.accountingPeriod,
            targetYear: params.targetYear,
            accumulated: params.accumulated,
            onConfirm: () => {
              periodDragTagging({
                period: params.accountingPeriod,
                targetYear: params.targetYear,
                accumulated: params.accumulated,
              });
              restoreSelection();
            },
            onCancel: () => {
              pop();
              restoreSelection();
            },
          });
        } else {
          handleSetPeriod({
            period: params.accountingPeriod,
            targetYear: params.targetYear,
            accumulated: params.accumulated,
          });
        }
      }
    };

  // 시점 옵션 (기초/기말/구분없음)
  const timingOptions = [
    {
      label: "기초",
      value: "BEGIN",
      isActive: cellTags?.period === AccountingPeriod.BEGIN,
      onClick: createPeriodHandler({
        accountingPeriod: itemToAccountingPeriod("기초" as BalanceTimingItem),
      }),
    },
    {
      label: "기말",
      value: "END",
      isActive: cellTags?.period === AccountingPeriod.END,
      onClick: createPeriodHandler({
        accountingPeriod: itemToAccountingPeriod("기말" as BalanceTimingItem),
      }),
    },
    {
      label: "구분없음",
      value: "NONE",
      isActive: cellTags?.period === null,
      onClick: createPeriodHandler({
        accountingPeriod: itemToAccountingPeriod(
          "구분없음" as BalanceTimingItem,
        ),
      }),
    },
  ];

  // 분기 옵션 (당기/전기/구분없음)
  const yearOptions = [
    {
      label: "당기",
      value: "CURRENT",
      isActive: cellTags?.targetYear === TargetYear.CURRENT,
      onClick: createPeriodHandler({
        targetYear: durationItemToTargetYear("당기" as DurationItem),
      }),
    },
    {
      label: "전기",
      value: "PRIOR",
      isActive: cellTags?.targetYear === TargetYear.PRIOR,
      onClick: createPeriodHandler({
        targetYear: durationItemToTargetYear("전기" as DurationItem),
      }),
    },
    {
      label: "구분없음",
      value: "NONE",
      isActive: cellTags?.targetYear === null,
      onClick: createPeriodHandler({
        targetYear: durationItemToTargetYear("구분없음" as DurationItem),
      }),
    },
  ];

  // 기간 옵션 (3개월/누적/구분없음)
  const periodOptions = [
    {
      label: "3개월",
      value: "QUARTER_ACC",
      isActive: cellTags?.accumulated === Accumulated.QUARTER_ACC,
      onClick: createPeriodHandler({
        accumulated: itemToAccumulation("3개월" as AccumulationItem),
      }),
    },
    {
      label: "누적",
      value: "ACC",
      isActive: cellTags?.accumulated === Accumulated.ACC,
      onClick: createPeriodHandler({
        accumulated: itemToAccumulation("누적" as AccumulationItem),
      }),
    },
    {
      label: "구분없음",
      value: "NONE",
      isActive: cellTags?.accumulated === null,
      onClick: createPeriodHandler({
        accumulated: itemToAccumulation("구분없음" as AccumulationItem),
      }),
    },
  ];

  return (
    <PopoverContent
      side="left"
      align="start"
      className="flex w-[300px] flex-col items-start rounded-2xl bg-white shadow-lg p-0 m-0 border-none"
    >
      {/* 헤더 */}
      <div className="flex h-[36px] justify-between items-center self-stretch border-b-[1px] border-b-neutral-100 py-[12px] pl-[16px] pr-[8px]">
        <span className="text-[#171717] label-1-normal-semibold">속성</span>
        <div className="flex w-[22px] h-[22px] py-[4px] px-[8px] justify-center items-center gap-[4px] rounded-[4px] cursor-pointer">
          <PopoverClose asChild>
            <X className="w-[14px] h-[14px] flex-shrink-0 aspect-square text-neutral-500" />
          </PopoverClose>
        </div>
      </div>

      {/* 콘텐츠 */}
      <div className="flex p-[16px] flex-col items-start gap-[12px] self-stretch">
        <ToggleGroup
          title="시점"
          options={timingOptions}
          isLoading={isFetching || isPuttingPeriod}
        />
        <ToggleGroup
          title="분기"
          options={yearOptions}
          isLoading={isFetching || isPuttingPeriod}
        />
        <ToggleGroup
          title="기간"
          options={periodOptions}
          isLoading={isFetching || isPuttingPeriod}
        />
      </div>
    </PopoverContent>
  );
};

export { MappingCellPropertyPopoverTrigger, MappingCellPropertyPopoverContent };
